import { Directive, EventEmitter, HostListener, Output, forwardRef } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, Validator } from '@angular/forms';

import { ToolsService } from '../services/tools.service';

@Directive({
    selector: '[iban]',
    providers: [{ provide: NG_VALIDATORS, useExisting: forwardRef(() => IbanInputDirective), multi: true }],
})
export class IbanInputDirective implements Validator {
    public ibanMask = {
        FR: 27,
        CY: 28,
        BE: 16,
        MC: 27,
        FI: 18,
        DE: 22,
        AT: 20,
        BG: 22,
        HR: 21,
        DK: 18,
        ES: 24,
        EE: 20,
        GI: 23,
        GR: 27,
        HU: 28,
        IE: 22,
        IS: 26,
        IT: 27,
        LV: 21,
        LI: 21,
        LT: 20,
        LU: 20,
        MT: 31,
        NO: 15,
        NL: 18,
        PL: 28,
        PT: 25,
        CZ: 24,
        RO: 24,
        GB: 22,
        SM: 27,
        SK: 24,
        SI: 19,
        SE: 24,
        CH: 21,
    };

    @Output() calculatedIbanLength = new EventEmitter<{ iban: string; longueur: number }>();

    @HostListener('input', ['$event']) onInput(event) {
        const ibanSaisi: string = event.target.value;
        let ibanObject: { iban: string; longueur: number } = { iban: '', longueur: 0 };

        if (ibanSaisi.length > 5 && !ibanSaisi.includes(' ')) {
            ibanObject = ToolsService.validateIban(ibanSaisi, this.ibanMask);
        } else {
            ibanObject.iban = ibanSaisi;
            ibanObject = ToolsService.validateLastCharacter(ibanObject, this.ibanMask);
        }
        event.target.value = ibanObject.iban;
        this.calculatedIbanLength.emit(ibanObject);
    }

    public validate(control: AbstractControl): { [key: string]: any } {
        let value: string = control.value || '';
        value = value.replace(/\s+/g, '');
        if (!value || value.length < 3) {
            return { invalidIban: true };
        }

        let parsedValue: string = String(value);

        const prefix: string = value.slice(0, 2).toUpperCase();
        const length = this.ibanMask[prefix];

        if (!length) {
            return { invalidIban: true };
        }

        // iban should be at least 15 characters long
        if (parsedValue.length !== length) {
            const message = prefix.padEnd(length, '1');
            const ibanObject = ToolsService.validateIban(message, this.ibanMask);
            return { invalidFormat: true, message: ibanObject.iban };
        }

        // test for illegal characters
        const regexp: RegExp = /^[a-zA-Z0-9]+$/;
        if (regexp.test(parsedValue) === false) {
            return { invalidIban: true };
        }

        // move the first four characters to the back and make sure everything is uppercase
        parsedValue = (parsedValue.substring(4) + parsedValue.substring(0, 4)).toUpperCase();
        let valueWithConvertedNumbers: string = '';
        for (let i = 0; i < parsedValue.length; i++) {
            const character: number = parsedValue.charCodeAt(i);

            // If the character is A-Z, we need to convert it to a number from 10-35
            if (character > 64 && character < 91) {
                valueWithConvertedNumbers += String(character - 55);
            } else {
                valueWithConvertedNumbers += String.fromCharCode(character);
            }
        }

        const modulo = this.modulo(valueWithConvertedNumbers, 97);
        if (modulo !== 1) {
            return { invalidIban: true };
        }

        return null;
    }

    // Modulo of large numbers
    // See https://stackoverflow.com/questions/929910/modulo-in-javascript-large-number
    modulo(divident, divisor): number {
        const partLength = 10;

        while (divident.length > partLength) {
            const part = divident.substring(0, partLength);
            divident = (part % divisor) + divident.substring(partLength);
        }

        return divident % divisor;
    }
}
